<template>
  <v-container>
    <v-layout
      row
      pt-10
    >
      <v-flex
        xs12
        sm6
        offset-sm3
      >
        <h2
          class="primary--text"
        >
          Edit {{ firstName }} {{ lastName }}
        </h2>
        <p>{{ email }}
          <v-btn
            @click="copyPhone()"
          >
            Copy Phone
          </v-btn>
          |
          <a
            :href="stripeLink"
            target="_blank"
          >
            Stripe Link
          </a>
          |
           Total Paid: ${{ totalPaid() }}
        </p>
        <span v-show="emergencyContactEntered == true">
          <p><b>Emergency Contact:</b> {{ emergencyContactName }} ({{ emergencyContactRelationship }}) <i>+{{ emergencyContactPhone }}</i></p>
        </span>
        <span v-show="emergencyContactEntered == false">
          <p><b>No emergency contact entered.</b></p>
        </span>
        <v-btn
          color="blue darken-1"
          dark
          mb-5
          @click="masqueradeAsDancer"
        >
          View as Dancer
        </v-btn>
        <v-btn
          outlined
          color="primary"
          class="ml-2"
          @click="emailIdentityOpen = true"
        >
          Link Emails
        </v-btn>
        <v-btn
          color="orange darken-2"
          dark
          class="ml-2"
          :loading="cancelLoading"
          @click="onCancelDancer"
        >
          ⊘ Cancel dancer
        </v-btn>
        <p class="mt-3 caption grey--text text--darken-1">
          Festival years on file:
          <strong>{{ yearsAttendedLabel }}</strong>
        </p>
        <v-btn
          outlined
          color="primary"
          class="mb-2"
          :loading="recalcLoading"
          @click="recalculateYearsAndHistory"
        >
          Recalculate years &amp; history
        </v-btn>
        <p v-if="recalcMessage" class="caption success--text">{{ recalcMessage }}</p>
        <p v-if="recalcError" class="caption error--text">{{ recalcError }}</p>
        <p class="caption grey--text">
          Updates attended years from the 2022–2023 roster and later enrollments.
          Festival history pieces still load when you view their dashboard.
        </p>
        <v-dialog
          v-model="dialog"
          width="500"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="red"
              dark
              v-bind="attrs"
              v-on="on"
              class="ml-2"
            >
              - Delete dancer
            </v-btn>
          </template>

          <v-card>
            <v-card-title class="text-h5 grey lighten-2">
              Are you sure?
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <p>Are you sure you'd like to delete this dancer?</p>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                text
                @click="deleteDancer()"
              >
                Yes, Delete
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-flex>
    </v-layout>
    <v-layout
      row
      pt-10
    >
      <v-flex
        xs12
        sm6
        offset-sm3
      >
        <h2>Travel Details</h2>
        <p v-show="travelDetailsEntered !== true && !hasAnyExtraNight">
          Full trip itinerary not entered yet
        </p>
        <p v-show="travelDetailsEntered !== true && hasAnyExtraNight" class="caption">
          Full trip itinerary not entered yet — extra nights on file below.
        </p>
        <h3><u>Lodging</u></h3>
        <p>{{ roomType }}<span v-if="!isOffsiteRoom && roomNumber"> · Room {{ roomNumber }}</span></p>
        <ul v-show="!isOffsiteRoom">
          <li>Check-in: {{ displayCheckIn }}</li>
          <li>Check-out: {{ displayCheckOut }}</li>
        </ul>
        <v-btn v-if="!isOffsiteRoom" small outlined color="primary" @click="openLodgingEditor">Edit lodging</v-btn>
        <span v-show="hasAnyExtraNight">
          <p>
            <br />
            Extra nights:
            <br />
            <i>Cancel for a full refund until May 6 by emailing hello@iabf.dance.</i>
          </p>
          <ul>
            <li v-for="night in extraNightLabelRows" :key="night.key" v-show="night.status">
              {{ night.label }} | Status: {{ night.status }}
            </li>
          </ul>
          <br />
          <span v-show="hasPendingExtraNights">
            <p><b>Extra nights pending approval.</b><span v-show="extraNightRemainingBalance === 0"> No payment due.</span></p>
          </span>
          <span v-show="extraNightRemainingBalance > 0">
            <p>Total: ${{ extraNightsTotal }}.
            <p><b>Remaining balance due: ${{ extraNightRemainingBalance }}</b></p>
            <p v-show="extraNightPaymentMethod === 'invoice'">
              <a
                :href="extraNightInvoiceLink"
                target="_blank"
              >
                {{ extraNightInvoiceLinkLabel }}
              </a>
            </p>
            <p v-show="extraNightPaymentMethod === 'charge'">Balance of ${{ extraNightRemainingBalance }} will be automatically charged to the card on file upon confirmation from the hotel.</p>
          </span>
        </span>
        <div v-if="extraNightPaymentAdjustments.length" class="billing-history">
          <h4 class="billing-history-title">Extra-night billing record</h4>
          <p class="billing-history-lede">
            When nights are confirmed, their cost is rolled into a registration installment (not a separate invoice). History:
          </p>
          <ul class="billing-history-list">
            <li v-for="adj in extraNightPaymentAdjustments" :key="adj.id">
              <strong>{{ formatAdjustmentDate(adj.createdAt) }}</strong>
              —
              {{ adj.amount >= 0 ? '+' : '−' }}${{ Math.abs(Number(adj.amount) || 0) }}
              on <strong>{{ adj.paymentLabel || adj.paymentSlot }}</strong>
              <span v-if="adj.nights && adj.nights.length"> ({{ adj.nights.join('; ') }})</span>
              <div class="billing-history-note">{{ adj.note }}</div>
              <div v-if="adj.previousDue != null && adj.newDue != null" class="billing-history-meta">
                Payment amount: ${{ adj.previousDue }} → ${{ adj.newDue }}
              </div>
            </li>
          </ul>
        </div>
        <br />
        <h3><u>Travel Details</u></h3>
        <span v-show="travelDetailsEntered">
          <ul>
            <li v-if="arrivalType === 'Plane'">Arriving at {{ arrivalDate }} {{ arrivalTime }} EST to {{ arrivalAirport }} on flight {{ arrivalFlightNumber }}</li>
            <li v-if="arrivalType === 'Car'">Driving in on {{ arrivalDate }} around {{ arrivalTime }} EST. Complimentary self-parking onsite.</li>
            <li v-if="departureType === 'Plane'">Departing on {{ departureDate }} at {{ departureTime }} EST from {{ departureAirport }} on flight {{ departureFlightNumber }}</li>
            <li v-if="departureType === 'Car'">Driving out on {{ departureDate }} around {{ departureTime }} EST. Complimentary self-parking onsite.</li>
          </ul>
          <ul v-show="arrivalSharedCar || departureSharedCar">
            <li v-if="arrivalSharedCar">Shared car requested on {{ arrivalDate }} around {{ arrivalTime }} from {{ arrivalAirport }}.</li>
            <li v-if="departureSharedCar">Shared car requested on {{ departureDate }} around {{ departureTime }} to {{ departureAirport }}.</li>
          </ul>
        </span>
      </v-flex>
    </v-layout>
    <v-layout
      row
      pt-10
    >
      <v-flex
        xs12
        sm6
        offset-sm3
      >
        <h2>Dietary Restrictions</h2>
        <span v-show="dietaryRestrictionsEntered == true">
          <ul>
            <li>{{ this.dietaryDisplay() }}</li>
            <li>Dietary Restrictions: {{ foodsNotConsumed.length == 0 ? '' : foodsNotConsumed.join(", ") }}</li>
            <li>Food Allergies: {{ foodsNotConsumed.length == 0 ? '' : foodAllergies.join(", ") }}</li>
            <li>Coffee/Tea Preferences: {{ coffee ? coffee : 'None entered' }}</li>
            <li>Other Dietary Notes: {{ dietaryNotes ? dietaryNotes : 'None entered' }}</li>
          </ul>
        </span>
        <p v-show="dietaryRestrictionsEntered == false">No dietary restrictions entered</p>
      </v-flex>
    </v-layout>
    <v-layout
      row
      pt-10
    >
      <v-flex
        xs12
      >
        <form @submit.prevent="onUpdateDancer(false)">
          <v-layout
            row
          >
            <v-flex
              xs12
              sm6
              offset-sm3
            >
            <h2>Edit Fields</h2>
            <v-text-field
                id="first-name"
                v-model="firstName"
                name="first-name"
                label="First Name"
                required
              />
              <v-text-field
                id="last-name"
                v-model="lastName"
                name="last-name"
                label="Last Name"
                required
              />
              <v-text-field
                id="email"
                v-model="email"
                name="email"
                label="Email"
                type="email"
                required
              />
              <v-text-field
                id="phone"
                v-model="phone"
                name="phone"
                label="Phone"
                type="tel"
              />
              <v-text-field
                id="addressStreet"
                v-model="addressStreet"
                name="addressStreet"
                label="Street address"
              />
              <v-text-field
                id="addressStreet2"
                v-model="addressStreet2"
                name="addressStreet2"
                label="Apt / suite"
              />
              <v-text-field
                id="addressCity"
                v-model="addressCity"
                name="addressCity"
                label="City"
              />
              <v-text-field
                id="addressState"
                v-model="addressState"
                name="addressState"
                label="State / province"
              />
              <v-text-field
                id="addressZip"
                v-model="addressZip"
                name="addressZip"
                label="ZIP / postal code"
              />
              <v-text-field
                id="addressCountry"
                v-model="addressCountry"
                name="addressCountry"
                label="Country"
              />
              <v-text-field
                id="stripeLink"
                v-model="stripeLink"
                name="stripeLink"
                label="Stripe Link"
              />
              <p>Registration Date</p>
              <v-date-picker
                v-model="registrationDate"
                label="Registration Date"
                required
              />
              <p class="gift-note">Returning-dancer free gifts (no early-bird gift for 2027 — expected sell-out).</p>
              <v-select
                id="registrationGift1"
                v-model="registrationGift1"
                name="registrationGift1"
                label="Free gift 1"
                :items="gifts"
              />
              <v-select
                id="registrationGift2"
                v-model="registrationGift2"
                name="registrationGift2"
                label="Free gift 2"
                :items="gifts"
              />
              <v-select
                id="registrationGift3"
                v-model="registrationGift3"
                name="registrationGift3"
                label="Free gift 3"
                :items="gifts"
              />
              <v-select
                id="registrationGift4"
                v-model="registrationGift4"
                name="registrationGift4"
                label="Free gift 4"
                :items="gifts"
              />
              <v-select
                id="room"
                v-model="room"
                name="room"
                label="Rooming with us?"
                :items="roomOptions"
                required
              />
              <v-text-field
                v-if="!isOffsiteRoom"
                id="roomNumber"
                v-model="roomNumber"
                name="roomNumber"
                label="Room number"
                placeholder="e.g. 12"
              />
              <v-select
                id="primaryRoomId"
                v-model="primaryRoomId"
                name="primaryRoomId"
                label="Roommate"
                :items="allDancers"
                item-text="name"
                item-value="id"
              />
              <v-text-field
                id="initialPayment"
                v-model="initialPayment"
                name="initialPayment"
                label="Initial Payment Received (USD)"
                type="number"
                required
              />
              <v-text-field
                id="initialPaymentLink"
                v-model="initialPaymentLink"
                name="initialPaymentLink"
                label="Invoice Link"
              />
              <v-checkbox
                id="installments"
                v-model="installments"
                name="installments"
                label="Pay in Installments"
              />
              <span v-show="installments">
                <v-text-field
                  id="payment1Due"
                  v-model="payment1Due"
                  name="payment1Due"
                  label="1st Scheduled Payment (USD)"
                  type="float"
                />
                <div v-if="notesForPaymentSlot('payment1').length" class="payment-slot-notes">
                  <div class="payment-slot-notes-label">Includes / notes</div>
                  <ul>
                    <li v-for="adj in notesForPaymentSlot('payment1')" :key="adj.id">
                      {{ adj.note }}
                      <span class="payment-slot-notes-date">{{ formatAdjustmentDate(adj.createdAt) }}</span>
                    </li>
                  </ul>
                </div>
                <v-select
                  id="payment1Status"
                  v-model="payment1Status"
                  name="payment1Status"
                  label="Status"
                  :items="['N/A', 'Paid', 'Overdue', 'Upcoming']"
                  required
                />
                <v-btn v-if="payment1Status !== 'Paid' && payment1Status !== 'N/A'" small :loading="charging[1]" :disabled="!!charging[1]" @click="chargePayment(1)">Charge ${{ payment1Due }}</v-btn>
                <span v-if="chargeError && chargeError.startsWith('1:')" class="charge-error">{{ chargeError.slice(2) }}</span>
                <v-text-field
                  id="payment1Link"
                  v-model="payment1Link"
                  name="payment1Link"
                  label="Invoice Link"
                />
                <v-text-field
                  id="payment2Due"
                  v-model="payment2Due"
                  name="payment2Due"
                  label="2nd Scheduled Payment (USD)"
                  type="float"
                />
                <div v-if="notesForPaymentSlot('payment2').length" class="payment-slot-notes">
                  <div class="payment-slot-notes-label">Includes / notes</div>
                  <ul>
                    <li v-for="adj in notesForPaymentSlot('payment2')" :key="adj.id">
                      {{ adj.note }}
                      <span class="payment-slot-notes-date">{{ formatAdjustmentDate(adj.createdAt) }}</span>
                    </li>
                  </ul>
                </div>
                <v-select
                  id="payment2Status"
                  v-model="payment2Status"
                  name="payment2Status"
                  label="Status"
                  :items="['N/A', 'Paid', 'Overdue', 'Upcoming']"
                  required
                />
                <v-btn v-if="payment2Status !== 'Paid' && payment2Status !== 'N/A'" small :loading="charging[2]" :disabled="!!charging[2]" @click="chargePayment(2)">Charge ${{ payment2Due }}</v-btn>
                <span v-if="chargeError && chargeError.startsWith('2:')" class="charge-error">{{ chargeError.slice(2) }}</span>
                <v-text-field
                  id="payment2Link"
                  v-model="payment2Link"
                  name="payment2Link"
                  label="Invoice Link"
                />
                <v-text-field
                  id="payment3Due"
                  v-model="payment3Due"
                  name="payment3Due"
                  label="3rd Scheduled Payment (USD)"
                  type="float"
                />
                <div v-if="notesForPaymentSlot('payment3').length" class="payment-slot-notes">
                  <div class="payment-slot-notes-label">Includes / notes</div>
                  <ul>
                    <li v-for="adj in notesForPaymentSlot('payment3')" :key="adj.id">
                      {{ adj.note }}
                      <span class="payment-slot-notes-date">{{ formatAdjustmentDate(adj.createdAt) }}</span>
                    </li>
                  </ul>
                </div>
                <v-select
                  id="payment3Status"
                  v-model="payment3Status"
                  name="payment3Status"
                  label="Status"
                  :items="['N/A', 'Paid', 'Overdue', 'Upcoming']"
                  required
                />
                <v-btn v-if="payment3Status !== 'Paid' && payment3Status !== 'N/A'" small :loading="charging[3]" :disabled="!!charging[3]" @click="chargePayment(3)">Charge ${{ payment3Due }}</v-btn>
                <span v-if="chargeError && chargeError.startsWith('3:')" class="charge-error">{{ chargeError.slice(2) }}</span>
                <v-text-field
                  id="payment3Link"
                  v-model="payment3Link"
                  name="payment3Link"
                  label="Invoice Link"
                />
                <v-text-field
                  id="payment4Due"
                  v-model="payment4Due"
                  name="payment4Due"
                  label="4th Scheduled Payment (USD)"
                  type="float"
                />
                <div v-if="notesForPaymentSlot('payment4').length" class="payment-slot-notes">
                  <div class="payment-slot-notes-label">Includes / notes</div>
                  <ul>
                    <li v-for="adj in notesForPaymentSlot('payment4')" :key="adj.id">
                      {{ adj.note }}
                      <span class="payment-slot-notes-date">{{ formatAdjustmentDate(adj.createdAt) }}</span>
                    </li>
                  </ul>
                </div>
                <v-select
                  id="payment4Status"
                  v-model="payment4Status"
                  name="payment4Status"
                  label="Status"
                  :items="['N/A', 'Paid', 'Overdue', 'Upcoming']"
                  required
                />
                <v-btn v-if="payment4Status !== 'Paid' && payment4Status !== 'N/A'" small :loading="charging[4]" :disabled="!!charging[4]" @click="chargePayment(4)">Charge ${{ payment4Due }}</v-btn>
                <span v-if="chargeError && chargeError.startsWith('4:')" class="charge-error">{{ chargeError.slice(2) }}</span>
                <v-text-field
                  id="payment4Link"
                  v-model="payment4Link"
                  name="payment4Link"
                  label="Invoice Link"
                />
                <v-text-field
                  id="payment5Due"
                  v-model="payment5Due"
                  name="payment5Due"
                  label="5th Scheduled Payment (USD)"
                  type="float"
                />
                <div v-if="notesForPaymentSlot('payment5').length" class="payment-slot-notes">
                  <div class="payment-slot-notes-label">Includes / notes</div>
                  <ul>
                    <li v-for="adj in notesForPaymentSlot('payment5')" :key="adj.id">
                      {{ adj.note }}
                      <span class="payment-slot-notes-date">{{ formatAdjustmentDate(adj.createdAt) }}</span>
                    </li>
                  </ul>
                </div>
                <v-select
                  id="payment5Status"
                  v-model="payment5Status"
                  name="payment5Status"
                  label="Status"
                  :items="['N/A', 'Paid', 'Overdue', 'Upcoming']"
                  required
                />
                <v-btn v-if="payment5Status !== 'Paid' && payment5Status !== 'N/A'" small :loading="charging[5]" :disabled="!!charging[5]" @click="chargePayment(5)">Charge ${{ payment5Due }}</v-btn>
                <span v-if="chargeError && chargeError.startsWith('5:')" class="charge-error">{{ chargeError.slice(2) }}</span>
                <v-text-field
                  id="payment5Link"
                  v-model="payment5Link"
                  name="payment5Link"
                  label="Invoice Link"
                />
                <div v-if="outstandingBalance > 0" class="outstanding-balance-alert">
                  <strong>Outstanding Balance: ${{ outstandingBalance }}</strong>
                  <span v-if="costumeBalance > 0 && guestBalance > 0">${{ costumeBalance }} costume + ${{ guestBalance }} guests — not yet included in scheduled payments</span>
                  <span v-else-if="costumeBalance > 0">${{ costumeBalance }} costume — not yet included in scheduled payments</span>
                  <span v-else>${{ guestBalance }} guests — not yet included in scheduled payments</span>
                </div>
                <v-text-field
                  id="payment6Due"
                  v-model="payment6Due"
                  name="payment6Due"
                  label="6th Scheduled Payment (USD)"
                  type="float"
                />
                <div v-if="notesForPaymentSlot('payment6').length" class="payment-slot-notes">
                  <div class="payment-slot-notes-label">Includes / notes</div>
                  <ul>
                    <li v-for="adj in notesForPaymentSlot('payment6')" :key="adj.id">
                      {{ adj.note }}
                      <span class="payment-slot-notes-date">{{ formatAdjustmentDate(adj.createdAt) }}</span>
                    </li>
                  </ul>
                </div>
                <v-select
                  id="payment6Status"
                  v-model="payment6Status"
                  name="payment6Status"
                  label="Status"
                  :items="['N/A', 'Paid', 'Overdue', 'Upcoming']"
                  required
                />
                <v-btn v-if="payment6Status !== 'Paid' && payment6Status !== 'N/A'" small :loading="charging[6]" :disabled="!!charging[6]" @click="chargePayment(6)">Charge ${{ payment6Due }}</v-btn>
                <span v-if="chargeError && chargeError.startsWith('6:')" class="charge-error">{{ chargeError.slice(2) }}</span>
                <v-text-field
                  id="payment6Link"
                  v-model="payment6Link"
                  name="payment6Link"
                  label="Invoice Link"
                />
              </span>
            </v-flex>
          </v-layout>
          <v-layout
            row
            pt-5
            mb-10
          >
            <v-flex
              xs12
              sm6
              offset-sm3
            >
              <v-btn
                class="primary"
                :disabled="!formIsValid"
                type="submit"
              >
                Update Dancer
              </v-btn>
            </v-flex>
          </v-layout>
        </form>
      </v-flex>
    </v-layout>

    <v-dialog v-model="lodgingEditorOpen" max-width="640" persistent>
      <v-card>
        <v-card-title>Edit lodging — {{ firstName }} {{ lastName }}</v-card-title>
        <v-card-text>
          <p class="muted">
            Toggle which extra nights this dancer is confirmed for.
            Festival window is {{ festivalWindowLabel }}; before- and after-nights extend that.
            Check-in/out and totals update from the toggles below.
            Checked nights save as <strong>confirmed</strong>.
          </p>
          <div class="lodging-nights">
            <div class="lodging-night-group">
              <div class="lodging-group-h">Before-nights (${{ ROOM_RATE }}/each)</div>
              <v-checkbox
                v-for="night in beforeNightOptions"
                :key="night.key"
                v-model="lodgingNights[night.key]"
                :label="night.label + (night.currentStatus ? ' · ' + night.currentStatus : '')"
                hide-details
                dense
                class="mt-0"
              />
            </div>
            <div class="lodging-night-group">
              <div class="lodging-group-h">After-nights (${{ ROOM_RATE }}/each)</div>
              <v-checkbox
                v-for="night in afterNightOptions"
                :key="night.key"
                v-model="lodgingNights[night.key]"
                :label="night.label + (night.currentStatus ? ' · ' + night.currentStatus : '')"
                hide-details
                dense
                class="mt-0"
              />
            </div>
          </div>

          <div class="lodging-preview">
            <div><strong>New check-in:</strong> {{ lodgingPreview.checkIn }}</div>
            <div><strong>New check-out:</strong> {{ lodgingPreview.checkOut }}</div>
            <div class="lodging-delta" v-if="lodgingPreview.delta !== 0">
              <strong>Change in cost:</strong>
              <span :class="lodgingPreview.delta > 0 ? 'cost-up' : 'cost-down'">
                {{ lodgingPreview.delta > 0 ? '+' : '−' }}${{ Math.abs(lodgingPreview.delta) }}
              </span>
              <span class="muted small">({{ lodgingPreview.oldNights }} → {{ lodgingPreview.newNights }} extra night{{ lodgingPreview.newNights === 1 ? '' : 's' }})</span>
            </div>
            <div v-else class="muted">No change in cost.</div>

            <div v-if="lodgingPreview.delta !== 0 && nextUnpaidPayment" class="lodging-payment">
              <strong>How this is billed</strong>
              <p class="lodging-payment-explain">
                Extra nights are <em>not</em> a separate invoice. Their cost is rolled into the dancer’s next unpaid
                registration installment so they pay lodging extras with that scheduled payment.
              </p>
              <p>
                <strong>{{ lodgingPreview.delta > 0 ? 'Add' : 'Subtract' }}
                ${{ Math.abs(lodgingPreview.delta) }}</strong>
                {{ lodgingPreview.delta > 0 ? 'to' : 'from' }}
                <strong>{{ nextUnpaidPayment.label }}</strong>
                (currently ${{ nextUnpaidPayment.due }} · {{ nextUnpaidPayment.status }})
                → new installment amount <strong>${{ Number(nextUnpaidPayment.due) + lodgingPreview.delta }}</strong>.
              </p>
              <p v-if="lodgingPreview.nightsPreview" class="lodging-payment-nights">
                Nights in this confirmation:
                <strong>{{ lodgingPreview.nightsPreview }}</strong>
              </p>
              <p class="lodging-payment-note-preview">
                A note will be saved on that payment and in the billing record, e.g.
                “{{ lodgingPreview.notePreview }}”
              </p>
              <div v-if="nextUnpaidPayment.status === 'Paid'" class="warn small">⚠ All scheduled payments are already paid — admin will need to collect this separately.</div>
            </div>
            <div v-else-if="lodgingPreview.delta !== 0" class="warn small">
              ⚠ No unpaid scheduled payment found. Adjust the installment manually after saving, and add a payment note so accounting knows why.
            </div>
          </div>

          <p v-if="lodgingSaveError" class="error--text mt-2">{{ lodgingSaveError }}</p>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="lodgingEditorOpen = false">Cancel</v-btn>
          <v-spacer />
          <v-btn color="primary" dark :loading="lodgingSaving" @click="saveLodgingEdit">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="emailIdentityOpen" width="560">
      <v-card>
        <v-card-title class="text-h5">Link emails — {{ firstName }} {{ lastName }}</v-card-title>
        <v-card-text>
          <iabf-person-email-identity
            :key="emailIdentityPersonId + '|' + email"
            :person-id="emailIdentityPersonId"
            :email="email"
            @updated="onEmailIdentityUpdated"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="emailIdentityOpen = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import { REGISTRATION_GIFT_ADMIN_OPTIONS } from '@/config/iabf-gifts'
import { functions } from '@/firebase'
import { festivalWeek } from '@/config/iabf-festival-dates'
import {
  computeCheckIn,
  computeCheckOut,
  extraNightIsoWindows,
  getDefaultCheckIn,
  getDefaultCheckOut
} from '@/utils/lodgingDates'
import { lodgingLabelFromRoom } from '@/utils/iabf-registration-package'
import { effectiveDeadlineIso, formatDeadlineDisplay } from '@/config/iabf-deadlines'
import IabfPersonEmailIdentity from '@/components/iabf/IabfPersonEmailIdentity'

export default {
  mixins: [iabfAdminMixin],
  components: { IabfPersonEmailIdentity },
    props: ['id', 'year'],
    data () {
      return {
        cancelLoading: false,
        emailIdentityOpen: false,
        personId: '',
        yearsAttended: [],
        recalcLoading: false,
        recalcMessage: '',
        recalcError: '',
        dietaryOptions: [
          {
            key: 'standard',
            description: 'Standard: Includes red meat, fish, poultry, eggs, milk, milk products and plant foods.'
          },
          {
            key: 'vegetarian',
            description: 'Vegetarian: Includes eggs, milk, milk products and plant foods, but excludes red meat, fish and poultry.'
          },
          {
            key: 'vegan',
            description: 'Vegan: Includes plant-based foods only and excludes animal foods of any kind.'
          },
          {
            key: 'pescetarian',
            description: 'Pescetarian: Includes fish, eggs, milk, milk products and plant foods, but excludes red meat and poultry.'
          }
        ],
        foodAllergiesOptions: ['No Allergies', 'Peanuts', 'Fish/Shellfish', 'Eggs', 'Peanut or nut butter', 'Soy products', 'All Dairy', 'Lactose Intolerant', 'Tree nuts (Walnuts, almonds, pecans etc.)', 'Mushrooms', 'Gluten', 'Other - use notes box below'],
        foodsNotConsumedOptions: ['No Restrictions', 'No Gluten - Preference', 'No Gluten - Celiac', 'No Red Meat', 'No Chicken', 'No Fish', 'No Eggs', 'No Pork', 'No Dairy Products', 'Other - use notes box below'],
        roomOptions: [
          { text: 'Standard Dorm', value: 'Standard Dorm' },
          { text: 'Larger Dorm', value: 'ADA Dorm' },
          { text: 'Cottage', value: 'Cottage' },
          { text: 'Staying with another dancer', value: 'Staying with another dancer' },
          { text: 'Staying offsite', value: 'Staying offsite' },
          // Legacy offsite values still on some enrollments
          { text: 'Staying offsite (shuttle)', value: 'Staying offsite (shuttle)' },
          { text: 'Staying offsite (no shuttle)', value: 'Staying offsite (no shuttle)' }
        ],
        gifts: REGISTRATION_GIFT_ADMIN_OPTIONS,
        dialog: false,
        charging: {},
        chargeError: '',
        lodgingEditorOpen: false,
        lodgingSaving: false,
        lodgingSaveError: '',
        lodgingNights: { extraNight1: false, extraNight2: false, extraNight3: false, extraNight4: false, extraNight5: false, extraNight6: false },
        travelDetailsId: null,
        allDancers: [],
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        addressStreet: '',
        addressStreet2: '',
        addressCity: '',
        addressState: '',
        addressZip: '',
        addressCountry: 'US',
        registrationDate: '',
        earlyBirdGift: 'None',
        registrationGift1: '',
        registrationGift2: '',
        registrationGift3: '',
        registrationGift4: '',
        room: '',
        roomNumber: '',
        primaryRoomId: '',
        initialPayment: '',
        installments: false,
        payment1Due: '',
        payment2Due: '',
        payment3Due: '',
        payment4Due: '',
        payment5Due: '',
        payment6Due: '',
        dietaryRestrictionsEntered: false,
        emergencyContactEntered: false,
        coffee: '',
        diet: '',
        dietaryNotes: '',
        foodsNotConsumed: [],
        foodAllergies: [],
        emergencyContactName: '',
        emergencyContactRelationship: '',
        emergencyContactPhone: '',
        travelDetailsEntered: false,
        arrivalType: '',
        arrivalAirport: '',
        arrivalFlightNumber: '',
        arrivalDate: '',
        arrivalTime: '',
        departureType: '',
        departureAirport: '',
        departureFlightNumber: '',
        departureDate: '',
        departureTime: '',
        extraNight1: '',
        extraNight2: '',
        extraNight3: '',
        extraNight4: '',
        extraNight5: '',
        extraNight6: '',
        arrivalSharedCar: '',
        departureSharedCar: '',
        checkIn: '',
        checkOut: '',
        extraNightsTotal: '',
        extraNightRemainingBalance: '',
        extraNightsBilledToPayments: 0,
        extraNightPaymentMethod: '',
        extraNightInvoiceLink: '',
        /** Append-only log of installment adjustments (extra nights, etc.) */
        paymentAdjustments: [],
        initialPaymentLink: '',
        payment1Status: 'Upcoming',
        payment1Link: '',
        payment2Status: 'Upcoming',
        payment2Link: '',
        payment3Status: 'Upcoming',
        payment3Link: '',
        payment4Status: 'Upcoming',
        payment4Link: '',
        payment5Status: 'Upcoming',
        payment5Link: '',
        payment6Status: 'Upcoming',
        payment6Link: '',
        stripeLink: '',
        costumeOrderCompleted: false,
        costumeOrderPaid: false,
        costumeTotalCost: 0
      }
    },
    computed: {
      ROOM_RATE: () => 150,
      festivalYear () {
        const y = Number(this.adminFestivalYear || this.year)
        return Number.isFinite(y) ? y : 2027
      },
      defaultCheckIn () {
        return getDefaultCheckIn(this.festivalYear)
      },
      defaultCheckOut () {
        return getDefaultCheckOut(this.festivalYear)
      },
      extraNightInvoiceLinkLabel () {
        const iso = effectiveDeadlineIso('extraNightInvoiceDue', this.festivalYear)
        if (!iso) {
          return 'Invoice'
        }
        return 'Invoice - Due ' + formatDeadlineDisplay(iso)
      },
      festivalWindowLabel () {
        const w = festivalWeek(this.festivalYear)
        // e.g. Mon 7/12 → Sat 7/17
        const short = (iso) => {
          if (!iso) return ''
          const parts = String(iso).slice(0, 10).split('-')
          if (parts.length < 3) return iso
          const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 12)
          const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
          return `${days[d.getDay()]} ${Number(parts[1])}/${Number(parts[2])}`
        }
        return `${short(w.arrivalIso)} → ${short(w.departureIso)}`
      },
      extraNightLabels () {
        return (extraNightIsoWindows(this.festivalYear).labels) || {}
      },
      extraNightLabelRows () {
        const labels = this.extraNightLabels
        const keys = ['extraNight1', 'extraNight2', 'extraNight3', 'extraNight4', 'extraNight5', 'extraNight6']
        const labelKeys = ['n1', 'n2', 'n3', 'n4', 'n5', 'n6']
        return keys.map((key, i) => ({
          key,
          label: labels[labelKeys[i]] || key,
          status: this[key] || ''
        }))
      },
      beforeNightOptions () {
        return this.extraNightLabelRows.slice(0, 3).map(n => ({
          ...n,
          currentStatus: n.status || ''
        }))
      },
      afterNightOptions () {
        return this.extraNightLabelRows.slice(3).map(n => ({
          ...n,
          currentStatus: n.status || ''
        }))
      },
      hasAnyExtraNight () {
        return this.extraNightLabelRows.some(n => !!n.status)
      },
      hasPendingExtraNights () {
        return this.extraNightLabelRows.some(n => n.status === 'requested')
      },
      isOffsiteRoom () {
        const r = (this.currentIABFDancer && this.currentIABFDancer.room) || this.room || ''
        return r === 'Staying Offsite' || r === 'Staying offsite' || String(r).indexOf('offsite') !== -1
      },
      countExtraNights () {
        return ['extraNight1', 'extraNight2', 'extraNight3', 'extraNight4', 'extraNight5', 'extraNight6']
          .reduce((n, k) => n + (this[k] ? 1 : 0), 0)
      },
      lodgingPreview () {
        // delta = (new $ owed for extra nights) − (what's already been
        // rolled into a scheduled payment by a previous admin save).
        // Tracked via extraNightsBilledToPayments on the travel-details
        // doc so repeated saves don't double-bill.
        const newFlags = { ...this.lodgingNights }
        const newNights = Object.values(newFlags).filter(Boolean).length
        const oldNights = this.countExtraNights
        const newTotal = newNights * this.ROOM_RATE
        const alreadyBilled = Number(this.extraNightsBilledToPayments) || 0
        const delta = newTotal - alreadyBilled
        const year = this.festivalYear
        const labels = this.extraNightLabels
        const labelKeys = ['n1', 'n2', 'n3', 'n4', 'n5', 'n6']
        const nightKeys = ['extraNight1', 'extraNight2', 'extraNight3', 'extraNight4', 'extraNight5', 'extraNight6']
        const selectedLabels = nightKeys
          .map((k, i) => (newFlags[k] ? (labels[labelKeys[i]] || k) : null))
          .filter(Boolean)
        const nightsPreview = selectedLabels.join('; ')
        const next = this.nextUnpaidPayment
        const sign = delta >= 0 ? '+' : '−'
        const abs = Math.abs(delta)
        const notePreview = next
          ? `${sign}$${abs} extra nights (${nightsPreview || `${newNights} night(s)`}) rolled into ${next.label} (was $${Number(next.due) || 0} → $${(Number(next.due) || 0) + delta})`
          : `${sign}$${abs} extra nights (${nightsPreview || `${newNights} night(s)`}) — no installment target`
        return {
          oldNights,
          newNights,
          newTotal,
          alreadyBilled,
          delta,
          nightsPreview,
          selectedLabels,
          notePreview,
          checkIn: computeCheckIn(newFlags, year),
          checkOut: computeCheckOut(newFlags, year)
        }
      },
      extraNightPaymentAdjustments () {
        return (this.paymentAdjustments || [])
          .filter(a => a && (a.type === 'extra-nights' || !a.type))
          .slice()
          .sort((a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0))
      },
      nextUnpaidPayment () {
        // Find the first scheduled payment that isn't fully paid yet.
        // Falls back to the latest non-N/A slot if all are marked Paid, so
        // the dialog can warn rather than silently no-op.
        const slots = ['payment1', 'payment2', 'payment3', 'payment4', 'payment5', 'payment6']
        const monthLabels = { payment1: 'Jan 5', payment2: 'Feb 5', payment3: 'Mar 5', payment4: 'Apr 5', payment5: 'May 5', payment6: 'June 5' }
        for (const slot of slots) {
          const status = this[slot + 'Status']
          if (!status || status === 'N/A') continue
          if (status !== 'Paid') {
            return { slot, status, due: Number(this[slot + 'Due']) || 0, label: `${monthLabels[slot]} payment` }
          }
        }
        // All paid — return the last one so the warning fires
        for (let i = slots.length - 1; i >= 0; i--) {
          const slot = slots[i]
          const status = this[slot + 'Status']
          if (status && status !== 'N/A') {
            return { slot, status, due: Number(this[slot + 'Due']) || 0, label: `${monthLabels[slot]} payment` }
          }
        }
        return null
      },
      roomType () {
        if (!this.currentIABFDancer) {
          return 'Loading'
        }
        const room = this.currentIABFDancer.room || this.room || ''
        if (room === 'Yes') {
          return 'Hotel Room Confirmed at Artist\'s Retreat'
        }
        if (room) {
          return lodgingLabelFromRoom(room) || room
        }
        return 'Room not set'
      },
      // Derive check-in/out from the extra-night flags rather than the stored
      // string, so older records (written before the check-out date was fixed)
      // still display correctly and always match the rooming-list export.
      extraNightFlags () {
        return {
          extraNight1: this.extraNight1,
          extraNight2: this.extraNight2,
          extraNight3: this.extraNight3,
          extraNight4: this.extraNight4,
          extraNight5: this.extraNight5,
          extraNight6: this.extraNight6
        }
      },
      displayCheckIn () {
        // Use extra nights whenever present (including request-only, before full trip form)
        const flags = this.hasAnyExtraNight ? this.extraNightFlags : null
        return computeCheckIn(flags, this.festivalYear)
      },
      displayCheckOut () {
        const flags = this.hasAnyExtraNight ? this.extraNightFlags : null
        return computeCheckOut(flags, this.festivalYear)
      },
      currentIABFDancer () {
        // Read state directly to bypass the masquerade override in the getter —
        // admin edit must always show the dancer matching the route :id, even
        // if an admin previously clicked "Dash" to masquerade as someone else.
        return this.$store.state.currentIABFDancer
      },
      formIsValid () {
        return this.firstName !== '' && this.lastName !== '' && this.email !== ''
      },
      costumeBalance () {
        return (this.costumeOrderCompleted && !this.costumeOrderPaid)
          ? (Number(this.costumeTotalCost) || 0)
          : 0
      },
      guestBalance () {
        return this.$store.getters.guestRegistrations
          .filter(g => g.paymentStatus !== 'paid')
          .reduce((sum, g) => sum + (Number(g.totalCost) || 0), 0)
      },
      outstandingBalance () {
        return this.costumeBalance + this.guestBalance
      },
      emailIdentityPersonId () {
        return this.personId || this.currentIABFDancer.personId || ''
      },
      yearsAttendedLabel () {
        const years = (this.yearsAttended || []).slice().sort((a, b) => a - b)
        return years.length ? years.join(', ') : 'none'
      }
    },
    created () {
      this.$store.dispatch('loadIABFDancer', { id: this.id, useAdminYear: true }).then(() => {
        this.firstName = this.currentIABFDancer.firstName
        this.lastName = this.currentIABFDancer.lastName
        this.email = this.currentIABFDancer.email
        this.personId = this.currentIABFDancer.personId || ''
        this.syncYearsFromPerson()
        this.$store.dispatch('loadGuestRegistrationsForDancer', (this.email || '').toLowerCase())
        this.phone = this.currentIABFDancer.phone
        this.addressStreet = this.currentIABFDancer.addressStreet || ''
        this.addressStreet2 = this.currentIABFDancer.addressStreet2 || ''
        this.addressCity = this.currentIABFDancer.addressCity || ''
        this.addressState = this.currentIABFDancer.addressState || ''
        this.addressZip = this.currentIABFDancer.addressZip || ''
        this.addressCountry = this.currentIABFDancer.addressCountry || 'US'
        this.registrationDate = this.currentIABFDancer.registrationDate
        this.registrationGift1 = this.currentIABFDancer.registrationGift1 || 'None'
        this.registrationGift2 = this.currentIABFDancer.registrationGift2 || 'None'
        this.registrationGift3 = this.currentIABFDancer.registrationGift3 || 'None'
        this.registrationGift4 = this.currentIABFDancer.registrationGift4 || 'None'
        this.earlyBirdGift = 'None'
        this.room = this.currentIABFDancer.room
        this.roomNumber = this.currentIABFDancer.roomNumber || ''
        this.primaryRoomId = this.currentIABFDancer.primaryRoomId || 'None'
        this.initialPayment = this.currentIABFDancer.initialPayment
        this.installments = this.currentIABFDancer.installments || true
        this.payment1Due = this.currentIABFDancer.payment1Due
        this.payment2Due = this.currentIABFDancer.payment2Due
        this.payment3Due = this.currentIABFDancer.payment3Due
        this.payment4Due = this.currentIABFDancer.payment4Due
        this.payment5Due = this.currentIABFDancer.payment5Due
        this.payment6Due = this.currentIABFDancer.payment6Due
        this.dietaryRestrictionsEntered = this.currentIABFDancer.dietaryRestrictionsEntered || false
        this.diet = this.currentIABFDancer.diet || ''
        this.coffee = this.currentIABFDancer.coffee
        this.foodsNotConsumed = this.currentIABFDancer.foodsNotConsumed || []
        this.foodAllergies = this.currentIABFDancer.foodAllergies || []
        this.dietaryNotes = this.currentIABFDancer.dietaryNotes
        this.emergencyContactName = this.currentIABFDancer.emergencyContactName
        this.emergencyContactRelationship = this.currentIABFDancer.emergencyContactRelationship
        this.emergencyContactPhone = this.currentIABFDancer.emergencyContactPhone
        this.emergencyContactEntered = this.currentIABFDancer.emergencyContactEntered || false
        this.initialPaymentLink = this.currentIABFDancer.initialPaymentLink || ''
        this.payment1Status = this.currentIABFDancer.payment1Status || 'Upcoming'
        this.payment1Link = this.currentIABFDancer.payment1Link || ''
        this.payment2Status = this.currentIABFDancer.payment2Status || 'Upcoming'
        this.payment2Link = this.currentIABFDancer.payment2Link || ''
        this.payment3Status = this.currentIABFDancer.payment3Status || 'Upcoming'
        this.payment3Link = this.currentIABFDancer.payment3Link || ''
        this.payment4Status = this.currentIABFDancer.payment4Status || 'Upcoming'
        this.payment4Link = this.currentIABFDancer.payment4Link || ''
        this.payment5Status = this.currentIABFDancer.payment5Status || 'Upcoming'
        this.payment5Link = this.currentIABFDancer.payment5Link || ''
        this.payment6Status = this.currentIABFDancer.payment6Status || 'Upcoming'
        this.payment6Link = this.currentIABFDancer.payment6Link || ''
        this.stripeLink = this.currentIABFDancer.stripeLink || ''
        this.costumeOrderCompleted = this.currentIABFDancer.costumeOrderCompleted || false
        this.costumeOrderPaid = this.currentIABFDancer.costumeOrderPaid || false
        this.costumeTotalCost = this.currentIABFDancer.costumeTotalCost || 0
        this.travelDetailsEntered = this.currentIABFDancer.travelDetailsEntered || false
        this.travelDetailsId = this.currentIABFDancer.travelDetailsId || null
        this.paymentAdjustments = Array.isArray(this.currentIABFDancer.paymentAdjustments)
          ? this.currentIABFDancer.paymentAdjustments.slice()
          : []
        // Always load travel details when linked — extra-nights-only requests
        // set travelDetailsId without travelDetailsEntered.
        this.loadTravelDetailsForAdmin()
      })
      this.$store.dispatch('loadIABFDancers').then(() => {
        this.$store.getters.loadedIABFDancers.forEach(dancer => {
          this.allDancers.push({
            id: dancer.id,
            name: dancer.firstName + ' ' + dancer.lastName
          })
        })
        this.allDancers.sort((a, b) => {
          if (a.name < b.name) {
            return -1
          }
          if (a.name > b.name) {
            return 1
          }
          return 0
        })
        this.allDancers.push({
          id: '',
          name: 'None'
        })
      })
    },
    methods: {
      applyTravelDetails (travelDetails) {
        if (!travelDetails) {
          return
        }
        if (travelDetails.id) {
          this.travelDetailsId = travelDetails.id
        }
        this.arrivalType = travelDetails.arrivalType || this.arrivalType
        this.arrivalAirport = travelDetails.arrivalAirport || this.arrivalAirport
        this.arrivalFlightNumber = travelDetails.arrivalFlightNumber || this.arrivalFlightNumber
        this.arrivalDate = travelDetails.arrivalDate || this.arrivalDate
        this.arrivalTime = travelDetails.arrivalTime || this.arrivalTime
        this.departureType = travelDetails.departureType || this.departureType
        this.departureAirport = travelDetails.departureAirport || this.departureAirport
        this.departureFlightNumber = travelDetails.departureFlightNumber || this.departureFlightNumber
        this.departureDate = travelDetails.departureDate || this.departureDate
        this.departureTime = travelDetails.departureTime || this.departureTime
        this.extraNight1 = travelDetails.extraNight1 || ''
        this.extraNight2 = travelDetails.extraNight2 || ''
        this.extraNight3 = travelDetails.extraNight3 || ''
        this.extraNight4 = travelDetails.extraNight4 || ''
        this.extraNight5 = travelDetails.extraNight5 || ''
        this.extraNight6 = travelDetails.extraNight6 || ''
        this.arrivalSharedCar = travelDetails.arrivalSharedCar
        this.departureSharedCar = travelDetails.departureSharedCar
        this.checkIn = travelDetails.checkIn
        this.checkOut = travelDetails.checkOut
        this.extraNightsTotal = travelDetails.extraNightsTotal
        this.extraNightRemainingBalance = travelDetails.extraNightRemainingBalance
        this.extraNightsBilledToPayments = Number(travelDetails.extraNightsBilledToPayments) || 0
        this.extraNightPaymentMethod = travelDetails.extraNightPaymentMethod
        this.extraNightInvoiceLink = travelDetails.extraNightInvoiceLink
        if (
          travelDetails.arrivalType ||
          travelDetails.arrivalAirport ||
          travelDetails.departureType ||
          travelDetails.departureAirport
        ) {
          this.travelDetailsEntered = true
        }
      },
      async loadTravelDetailsForAdmin () {
        const dancer = this.currentIABFDancer || {}
        const travelId = this.travelDetailsId || dancer.travelDetailsId || null
        // Use admin year collections (this.IABF) so 2026 wrap-up and 2027
        // registration both resolve correctly — store dancer helpers follow
        // the logged-in dancer year, not the admin switcher.
        try {
          const { db } = await import('@/firebase')
          const col = this.IABF.travelDetails
          if (!col) {
            return
          }
          const hasNights = (td) => {
            if (!td) return false
            return ['extraNight1', 'extraNight2', 'extraNight3', 'extraNight4', 'extraNight5', 'extraNight6']
              .some(k => !!td[k])
          }
          const hasTrip = (td) => !!(td && (td.arrivalType || td.arrivalAirport || td.departureType || td.departureAirport))
          let travelDetails = null
          if (travelId) {
            const snap = await db.collection(col).doc(travelId).get()
            if (snap.exists) {
              travelDetails = { ...snap.data(), id: snap.id }
            }
          }
          if (!hasNights(travelDetails) && !hasTrip(travelDetails) && this.id) {
            const q = await db.collection(col).where('personId', '==', this.id).get()
            if (!q.empty) {
              // Prefer the doc with the most populated fields (extra nights count)
              let best = null
              let bestScore = -1
              q.docs.forEach(doc => {
                const data = { ...doc.data(), id: doc.id }
                const score = Object.values(data).filter(v => v !== null && v !== undefined && v !== '' && v !== false).length
                if (score > bestScore) {
                  bestScore = score
                  best = data
                }
              })
              if (best) {
                travelDetails = best
              }
            }
          }
          if (travelDetails) {
            this.applyTravelDetails(travelDetails)
            // Keep enrollment linked if we found the doc by personId
            if (travelDetails.id && travelDetails.id !== dancer.travelDetailsId) {
              this.travelDetailsId = travelDetails.id
              try {
                await db.collection(this.IABF.dancers).doc(this.id).update({
                  travelDetailsId: travelDetails.id
                })
              } catch (e) {
                console.warn('Could not back-link travelDetailsId', e)
              }
            }
          }
        } catch (e) {
          console.warn('Admin travel details load failed', e)
        }
      },
      notesForPaymentSlot (slot) {
        return (this.paymentAdjustments || [])
          .filter(a => a && a.paymentSlot === slot)
          .slice()
          .sort((a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0))
      },
      formatAdjustmentDate (ts) {
        if (!ts) {
          return ''
        }
        const d = new Date(Number(ts))
        if (Number.isNaN(d.getTime())) {
          return ''
        }
        return d.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      },
      openLodgingEditor () {
        // Seed dialog state from whatever's currently on the dancer's
        // travel-details doc (the parent component already exposed each
        // extraNight* field for the read-only summary above).
        this.lodgingNights = {
          extraNight1: !!this.extraNight1,
          extraNight2: !!this.extraNight2,
          extraNight3: !!this.extraNight3,
          extraNight4: !!this.extraNight4,
          extraNight5: !!this.extraNight5,
          extraNight6: !!this.extraNight6
        }
        this.lodgingSaveError = ''
        this.lodgingEditorOpen = true
      },
      async saveLodgingEdit () {
        this.lodgingSaving = true
        this.lodgingSaveError = ''
        try {
          const { db } = await import('@/firebase')
          const flags = { ...this.lodgingNights }
          const newNightCount = Object.values(flags).filter(Boolean).length
          const newTotal = newNightCount * this.ROOM_RATE
          const alreadyBilled = Number(this.extraNightsBilledToPayments) || 0
          const delta = newTotal - alreadyBilled
          const year = this.festivalYear
          const preview = this.lodgingPreview
          const selectedLabels = preview.selectedLabels || []

          // Travel-details doc update — admin confirm sets "confirmed".
          // Clear unchecked nights. Recompute check-in/out + totals.
          // extraNightsBilledToPayments is bumped to newTotal so a repeat
          // save with no changes doesn't double-bill.
          const travelPayload = {
            personId: this.id,
            personType: 'dancer',
            extraNight1: flags.extraNight1 ? 'confirmed' : '',
            extraNight2: flags.extraNight2 ? 'confirmed' : '',
            extraNight3: flags.extraNight3 ? 'confirmed' : '',
            extraNight4: flags.extraNight4 ? 'confirmed' : '',
            extraNight5: flags.extraNight5 ? 'confirmed' : '',
            extraNight6: flags.extraNight6 ? 'confirmed' : '',
            checkIn: computeCheckIn(flags, year),
            checkOut: computeCheckOut(flags, year),
            extraNightsTotal: newTotal,
            extraNightRemainingBalance: 0,
            extraNightsBilledToPayments: newTotal,
            updatedAt: Date.now()
          }
          let docId = this.travelDetailsId
          if (docId) {
            await db.collection(this.IABF.travelDetails).doc(docId).set(travelPayload, { merge: true })
          } else {
            const ref = await db.collection(this.IABF.travelDetails).add({
              ...travelPayload,
              createdAt: Date.now()
            })
            docId = ref.id
            this.travelDetailsId = docId
            await db.collection(this.IABF.dancers).doc(this.id).update({
              travelDetailsId: docId
            })
          }

          // Roll the delta into the dancer's next unpaid scheduled payment
          // and write a durable note so accounting can see *why* the amount changed.
          const next = this.nextUnpaidPayment
          const dancerUpdate = {}
          let adjustment = null
          if (delta !== 0 && next && next.status !== 'Paid') {
            const previousDue = Number(next.due) || 0
            const newDue = Math.max(0, previousDue + delta)
            const sign = delta >= 0 ? '+' : '−'
            const abs = Math.abs(delta)
            const note =
              `${sign}$${abs} extra nights` +
              (selectedLabels.length ? ` (${selectedLabels.join('; ')})` : ` (${newNightCount} night(s))`) +
              ` rolled into ${next.label}` +
              ` (was $${previousDue} → $${newDue})`
            adjustment = {
              id: `adj-${Date.now()}`,
              type: 'extra-nights',
              amount: delta,
              paymentSlot: next.slot,
              paymentLabel: next.label,
              nights: selectedLabels,
              nightCount: newNightCount,
              previousDue,
              newDue,
              note,
              createdAt: Date.now()
            }
            const nextAdjustments = [adjustment].concat(this.paymentAdjustments || [])
            dancerUpdate[next.slot + 'Due'] = newDue
            dancerUpdate.paymentAdjustments = nextAdjustments
            // Compact human-readable note field on the payment slot (for exports / quick scan)
            const noteKey = next.slot + 'Note'
            const priorNote = (this.currentIABFDancer && this.currentIABFDancer[noteKey]) || ''
            dancerUpdate[noteKey] = priorNote
              ? `${priorNote}\n${note}`
              : note

            await db.collection(this.IABF.dancers).doc(this.id).update(dancerUpdate)
            this[next.slot + 'Due'] = newDue
            this.paymentAdjustments = nextAdjustments

            // Mirror log on travel-details for lodging-side audit trail
            try {
              const logSnap = await db.collection(this.IABF.travelDetails).doc(docId).get()
              const existingLog = (logSnap.exists && Array.isArray(logSnap.data().extraNightBillingLog))
                ? logSnap.data().extraNightBillingLog
                : []
              await db.collection(this.IABF.travelDetails).doc(docId).set({
                extraNightBillingLog: [adjustment].concat(existingLog)
              }, { merge: true })
            } catch (logErr) {
              console.warn('Could not write extraNightBillingLog', logErr)
            }
          } else if (delta !== 0) {
            // No installment to roll into — still record a note for accounting
            adjustment = {
              id: `adj-${Date.now()}`,
              type: 'extra-nights',
              amount: delta,
              paymentSlot: null,
              paymentLabel: 'Manual collection needed',
              nights: selectedLabels,
              nightCount: newNightCount,
              previousDue: null,
              newDue: null,
              note: `${delta >= 0 ? '+' : '−'}$${Math.abs(delta)} extra nights (${selectedLabels.join('; ') || newNightCount + ' night(s)'}) — no unpaid installment; collect separately`,
              createdAt: Date.now()
            }
            const nextAdjustments = [adjustment].concat(this.paymentAdjustments || [])
            await db.collection(this.IABF.dancers).doc(this.id).update({
              paymentAdjustments: nextAdjustments
            })
            this.paymentAdjustments = nextAdjustments
          }

          // Reflect new state locally so the dialog closes onto fresh data.
          this.extraNight1 = travelPayload.extraNight1
          this.extraNight2 = travelPayload.extraNight2
          this.extraNight3 = travelPayload.extraNight3
          this.extraNight4 = travelPayload.extraNight4
          this.extraNight5 = travelPayload.extraNight5
          this.extraNight6 = travelPayload.extraNight6
          this.extraNightsTotal = newTotal
          this.extraNightRemainingBalance = 0
          this.extraNightsBilledToPayments = newTotal
          this.lodgingEditorOpen = false
        } catch (e) {
          console.error(e)
          this.lodgingSaveError = e.message || 'Save failed.'
        }
        this.lodgingSaving = false
      },
      syncYearsFromPerson () {
        const person = this.$store.getters.currentIABFPerson || {}
        const dancer = this.currentIABFDancer || {}
        const raw = (person.yearsAttended && person.yearsAttended.length)
          ? person.yearsAttended
          : (dancer.yearsAttended || [])
        this.yearsAttended = (raw || []).map(Number).filter(n => Number.isFinite(n) && n > 0)
      },
      async recalculateYearsAndHistory () {
        this.recalcLoading = true
        this.recalcError = ''
        this.recalcMessage = ''
        try {
          const person = await this.$store.dispatch('recalculatePersonAttendanceYears', {
            personId: this.personId || (this.currentIABFDancer && this.currentIABFDancer.personId),
            email: this.email,
            dancer: {
              id: this.id,
              email: this.email,
              firstName: this.firstName,
              lastName: this.lastName,
              festivalYear: this.$store.getters.adminFestivalYear,
              personId: this.personId
            }
          })
          this.yearsAttended = (person && person.yearsAttended) || []
          this.recalcMessage = this.yearsAttended.length
            ? ('Years on file: ' + this.yearsAttended.slice().sort((a, b) => a - b).join(', '))
            : 'No attended years on file.'
        } catch (e) {
          this.recalcError = (e && e.message) || 'Recalculate failed.'
        }
        this.recalcLoading = false
      },
      masqueradeAsDancer () {
        const fromList = (this.$store.getters.loadedIABFDancers || []).find(d => d.id === this.id)
        this.$store.commit('setMasqueradeDancer', fromList || {
          id: this.id,
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          festivalYear: this.$store.getters.adminFestivalYear
        })
        this.$store.dispatch('refreshEffectiveDancer').catch(() => {})
        this.$router.push('/')
      },
      onEmailIdentityUpdated (result) {
        if (result && result.email) {
          this.email = result.email
        }
        if (result && result.personId) {
          this.personId = result.personId
        }
        this.$store.dispatch('loadIABFDancer', { id: this.id, useAdminYear: true }).catch(() => {})
      },
      copyPhone () {
        navigator.clipboard.writeText(this.phone)
      },
      totalPaid () {
        return parseFloat(this.initialPayment) + parseFloat(this.payment1Due) + parseFloat(this.payment2Due) + parseFloat(this.payment3Due) + parseFloat(this.payment4Due) + parseFloat(this.payment5Due) + parseFloat(this.payment6Due)
      },
      dietaryDisplay () {
        if (!this.dietaryRestrictionsEntered) {
          return 'No dietary restrictions entered'
        }
        return this.dietaryOptions.find(option => option.key === this.diet).description
      },
      async deleteDancer () {
        try {
          await this.$store.dispatch('deleteIABFDancer', this.id)
        } catch (e) {
          console.error('delete dancer failed:', e)
          // eslint-disable-next-line no-alert
          window.alert('Delete failed: ' + (e && e.message ? e.message : e))
          return
        }
        this.$router.push('/iabf/admin')
      },
      async onCancelDancer () {
        // eslint-disable-next-line no-alert
        const reason = window.prompt('Cancel this dancer? Optional reason (e.g. "refunded — replaced by ___"):')
        if (reason === null) return
        this.cancelLoading = true
        try {
          await this.$store.dispatch('cancelDancer', { dancerId: this.id, cancelReason: reason || '' })
          this.$router.push('/iabf/admin/canceled-dancers')
        } catch (e) {
          console.error('cancel failed:', e)
          // eslint-disable-next-line no-alert
          window.alert('Cancel failed: ' + (e && e.message ? e.message : e))
        }
        this.cancelLoading = false
      },
      onUpdateDancerGroupRoles () {
        if (!this.formIsValid) {
          return
        }
        const dancerData = {
          id: this.id,
          firstRole: this.firstRole,
          secondRole: this.secondRole
        }
        this.$store.dispatch('updateIABFDancerGroupRoles', dancerData)
        this.$router.push('/iabf/admin')
      },
      async chargePayment (paymentNumber) {
        const amounts = {
          1: this.payment1Due, 2: this.payment2Due, 3: this.payment3Due,
          4: this.payment4Due, 5: this.payment5Due, 6: this.payment6Due
        }
        const amount = amounts[paymentNumber]
        if (!amount || parseFloat(amount) <= 0) {
          return
        }
        this.chargeError = ''
        this.$set(this.charging, paymentNumber, true)
        try {
          const chargeIABFDancer = functions.httpsCallable('chargeIABFDancer')
          const result = await chargeIABFDancer({
            dancerId: this.id,
            amountDollars: amount,
            description: `IABF ${this.adminFestivalYear} Payment ${paymentNumber} — ${this.firstName} ${this.lastName}`
          })
          // Success: fill in receipt URL and mark Paid, then save
          const linkKey = paymentNumber === 0 ? 'initialPaymentLink' : `payment${paymentNumber}Link`
          const statusKey = `payment${paymentNumber}Status`
          this[linkKey] = result.data.receiptUrl
          this[statusKey] = 'Paid'
          this.onUpdateDancer(true)
        } catch (err) {
          // Declined or other error: mark Overdue and save
          this[`payment${paymentNumber}Status`] = 'Overdue'
          this.chargeError = `${paymentNumber}:${err.message || 'Charge failed'}`
          this.onUpdateDancer(true)
        } finally {
          this.$set(this.charging, paymentNumber, false)
        }
      },
      async onUpdateDancer (silent = false) {
        if (!this.formIsValid) {
          return
        }
        const dancerData = {
          id: this.id,
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          phone: this.phone,
          addressStreet: this.addressStreet,
          addressStreet2: this.addressStreet2,
          addressCity: this.addressCity,
          addressState: this.addressState,
          addressZip: this.addressZip,
          addressCountry: this.addressCountry,
          registrationDate: this.registrationDate,
          registrationGift1: this.registrationGift1,
          registrationGift2: this.registrationGift2,
          registrationGift3: this.registrationGift3,
          registrationGift4: this.registrationGift4,
          earlyBirdGift: 'None',
          room: this.room,
          roomNumber: this.roomNumber,
          primaryRoomId: this.primaryRoomId,
          initialPayment: this.initialPayment,
          initialPaymentLink: this.initialPaymentLink,
          installments: this.installments,
          payment1Due: this.payment1Due,
          payment1Status: this.payment1Status,
          payment1Link: this.payment1Link,
          payment2Due: this.payment2Due,
          payment2Status: this.payment2Status,
          payment2Link: this.payment2Link,
          payment3Due: this.payment3Due,
          payment3Status: this.payment3Status,
          payment3Link: this.payment3Link,
          payment4Due: this.payment4Due,
          payment4Status: this.payment4Status,
          payment4Link: this.payment4Link,
          payment5Due: this.payment5Due,
          payment5Status: this.payment5Status,
          payment5Link: this.payment5Link,
          payment6Due: this.payment6Due,
          payment6Status: this.payment6Status,
          payment6Link: this.payment6Link,
          stripeLink: this.stripeLink
        }
        try {
          await this.$store.dispatch('updateIABFDancer', dancerData)
        } catch (e) {
          console.error('update dancer failed:', e)
          // eslint-disable-next-line no-alert
          window.alert('Save failed: ' + (e && e.message ? e.message : e))
          return
        }
        if (!silent) {
          this.$router.push('/iabf/admin')
        }
      }
    }
  }
</script>

<style scoped>
.lodging-nights { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin: 14px 0 20px; }
.lodging-night-group { background: #faf8f3; padding: 12px 14px; border-radius: 6px; }
.lodging-group-h { font-weight: 700; font-size: 13px; color: #555; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em; }
.lodging-preview { background: #eef6f8; border: 1px solid #c9e0e6; border-radius: 6px; padding: 12px 14px; margin-top: 6px; }
.lodging-preview > div { margin-bottom: 4px; font-size: 14px; }
.lodging-delta .cost-up { color: #c43a54; font-weight: 700; }
.lodging-delta .cost-down { color: #2e7d32; font-weight: 700; }
.lodging-payment { margin-top: 10px; padding-top: 10px; border-top: 1px solid #c9e0e6; font-size: 14px; }
.lodging-payment-explain { margin: 6px 0 10px; color: #3d4a4f; line-height: 1.45; }
.lodging-payment-nights { margin: 8px 0 0; }
.lodging-payment-note-preview {
  margin: 10px 0 0;
  padding: 8px 10px;
  background: #fff;
  border: 1px dashed #b7c9cf;
  border-radius: 4px;
  font-size: 13px;
  color: #334;
  line-height: 1.4;
}
.warn { color: #92400e; background: #fef3c7; padding: 6px 10px; border-radius: 4px; margin-top: 6px; }
.billing-history {
  margin: 16px 0 8px;
  padding: 12px 14px;
  background: #f7f5ef;
  border: 1px solid #e6e1d6;
  border-radius: 8px;
}
.billing-history-title {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
}
.billing-history-lede {
  margin: 0 0 10px;
  font-size: 13px;
  color: #666;
  line-height: 1.4;
}
.billing-history-list {
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
}
.billing-history-list li { margin-bottom: 10px; }
.billing-history-note { margin-top: 2px; color: #333; }
.billing-history-meta { margin-top: 2px; font-size: 12px; color: #777; }
.payment-slot-notes {
  margin: -6px 0 14px;
  padding: 8px 12px;
  background: #f3f7f4;
  border-left: 3px solid #4c8c6a;
  border-radius: 0 4px 4px 0;
  font-size: 13px;
  line-height: 1.4;
}
.payment-slot-notes-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #2f6b4f;
  margin-bottom: 4px;
}
.payment-slot-notes ul {
  margin: 0;
  padding-left: 16px;
}
.payment-slot-notes li { margin-bottom: 4px; }
.payment-slot-notes-date {
  display: inline-block;
  margin-left: 6px;
  color: #888;
  font-size: 12px;
}
.warn.small { font-size: 13px; }
.muted { color: #888; }
.muted.small { font-size: 12px; }
.outstanding-balance-alert {
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: #fff5f5;
  border: 1px solid #ffcdd2;
  border-left: 4px solid #c62828;
  border-radius: 4px;
  padding: 10px 14px;
  margin-bottom: 16px;
  color: #c62828;
}

.outstanding-balance-alert strong {
  font-size: 16px;
}

.charge-error {
  display: block;
  color: #c62828;
  font-size: 13px;
  margin: 4px 0 8px;
}

.outstanding-balance-alert span {
  font-size: 13px;
  color: #b71c1c;
  opacity: 0.8;
}
</style>
